import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert, Box, Button, Checkbox, Chip, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle,
  IconButton, Paper, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, TextField, Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import { Link as RouterLink } from 'react-router-dom';
import { AuthService } from '../../services/auth.service';
import { useToast } from '../../context/ToastContext';
import { AccountService } from '../../services/account.service';
import { CwipService } from '../../services/cwip.service';
import { FixedAssetCategoryService } from '../../services/fixedAssetCategory.service';
import {
  CwipCapitaliseAsset, CwipLine, CwipProject, FixedAssetCategory,
} from '../../interfaces/fixedAsset/cwip.interface';
import { Account } from '../../interfaces/accounting.interface';
import { postingAccounts } from '../../utils/accountHierarchy';
import SearchableSelect from '../common/SearchableSelect';
import { AccountAutocomplete } from '../common/AccountAutocomplete';
import { formatDate, toLocalIsoDate } from '../../utils/dateUtils';
import { usePermission } from '../../hooks/usePermission';
import { PERMISSIONS } from '../../constants/permissions';

const money = (n: number) =>
  (n ?? 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const STATUS_COLOR: Record<string, 'default' | 'success' | 'info' | 'warning'> = {
  OPEN: 'info', CAPITALISED: 'success', CANCELLED: 'default',
};

type AssetRow = { name: string; categoryId: string; amount: string };

const CwipProjects: React.FC = () => {
  const canManage = usePermission(PERMISSIONS.FIXED_ASSET_MANAGE);
  const toast = useToast();
  const [rows, setRows] = useState<CwipProject[]>([]);
  const [accounts, setAccounts] = useState<Account[]>([]);
  const [categories, setCategories] = useState<FixedAssetCategory[]>([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [newName, setNewName] = useState('');
  const [newAccount, setNewAccount] = useState('');
  const [detail, setDetail] = useState<CwipProject | null>(null);
  const [linkable, setLinkable] = useState<CwipLine[] | null>(null);
  const [picked, setPicked] = useState<Set<string>>(new Set());
  const [capOpen, setCapOpen] = useState(false);
  const [capDate, setCapDate] = useState(toLocalIsoDate());
  const [assetRows, setAssetRows] = useState<AssetRow[]>([{ name: '', categoryId: '', amount: '' }]);
  const [busy, setBusy] = useState(false);

  const load = async () => {
    const institutionId = AuthService.getSelectedInstitution()!;
    try {
      setLoading(true);
      const [list, accs, cats] = await Promise.all([
        CwipService.list(institutionId),
        AccountService.getLookup().catch(() => [] as Account[]),
        FixedAssetCategoryService.list(institutionId).catch(() => [] as FixedAssetCategory[]),
      ]);
      setRows(list);
      setAccounts(accs ?? []);
      setCategories(cats.filter(c => c.active));
    } catch (e) {
      toast.showApiError(e, 'Failed to load CWIP projects');
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const assetAccounts = useMemo(
    () => postingAccounts(accounts).filter(a => a.accountType === 'ASSET'), [accounts]);

  const open = async (id: string) => {
    try {
      setDetail(await CwipService.get(id));
      setLinkable(null);
      setPicked(new Set());
    } catch (e) {
      toast.showApiError(e, 'Failed to load project');
    }
  };

  const run = async (fn: () => Promise<CwipProject>, ok: string) => {
    try {
      setBusy(true);
      const updated = await fn();
      setDetail(updated);
      toast.showSuccess(ok);
      await load();
    } catch (e) {
      toast.showApiError(e, 'Action failed');
    } finally {
      setBusy(false);
    }
  };

  const createProject = async () => {
    const institutionId = AuthService.getSelectedInstitution()!;
    try {
      setBusy(true);
      const p = await CwipService.create({
        institutionId, name: newName.trim(), cwipAccountId: newAccount || undefined,
      });
      setCreating(false);
      setNewName('');
      setNewAccount('');
      await load();
      setDetail(p);
    } catch (e) {
      toast.showApiError(e, 'Failed to create project');
    } finally {
      setBusy(false);
    }
  };

  const showPicker = async () => {
    if (!detail) return;
    try {
      setLinkable(await CwipService.linkable(detail.id));
      setPicked(new Set());
    } catch (e) {
      toast.showApiError(e, 'Failed to load CWIP lines');
    }
  };

  const toggle = (id: string) => setPicked(prev => {
    const next = new Set(prev);
    if (next.has(id)) next.delete(id); else next.add(id);
    return next;
  });

  const assetSum = assetRows.reduce((s, r) => s + (parseFloat(r.amount) || 0), 0);
  const capOk = detail && Math.abs(assetSum - detail.linkedTotal) < 0.005
    && assetRows.every(r => r.name.trim() && parseFloat(r.amount) > 0);

  const capitalise = async () => {
    if (!detail) return;
    const assets: CwipCapitaliseAsset[] = assetRows.map(r => ({
      name: r.name.trim(), categoryId: r.categoryId || undefined, amount: parseFloat(r.amount),
    }));
    await run(() => CwipService.capitalise(detail.id, { capitalisationDate: capDate, assets }),
      'Project capitalised');
    setCapOpen(false);
  };

  if (loading) return <Box sx={{ p: 3 }}><CircularProgress /></Box>;

  const isOpen = detail?.status === 'OPEN';

  return (
    <Box sx={{ p: 3 }} data-help-page="fixedAssets.cwip.page">
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="h5">Capital Work in Progress</Typography>
        {canManage && <Button data-help="fixedAssets.cwip.new" variant="contained" startIcon={<AddIcon />} onClick={() => setCreating(true)}>New project</Button>}
      </Box>
      <TableContainer component={Paper}>
        <Table size="small" data-help-section="fixedAssets.cwip.table">
          <TableHead>
            <TableRow>
              <TableCell data-help="fixedAssets.cwip.col.project">Project</TableCell><TableCell data-help="fixedAssets.cwip.col.status">Status</TableCell>
              <TableCell data-help="fixedAssets.cwip.col.lines" align="right">Cost lines</TableCell><TableCell data-help="fixedAssets.cwip.col.cost" align="right">Linked cost</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.length === 0 && <TableRow><TableCell colSpan={4} align="center">No CWIP projects yet</TableCell></TableRow>}
            {rows.map(p => (
              <TableRow key={p.id} hover sx={{ cursor: 'pointer' }} onClick={() => open(p.id)}>
                <TableCell>{p.name}</TableCell>
                <TableCell><Chip size="small" label={p.status} color={STATUS_COLOR[p.status]} /></TableCell>
                <TableCell align="right">{p.linkedLineCount}</TableCell>
                <TableCell align="right">{money(p.linkedTotal)}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      <Dialog data-help-page="fixedAssets.cwipNew.page" open={creating} onClose={() => setCreating(false)} fullWidth maxWidth="sm">
        <DialogTitle>New CWIP project</DialogTitle>
        <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 2 }}>
          <TextField data-help="fixedAssets.cwipNew.field.name" label="Name" required value={newName} onChange={e => setNewName(e.target.value)} sx={{ mt: 1 }} fullWidth />
          <AccountAutocomplete data-help="fixedAssets.cwipNew.field.account" accounts={assetAccounts} value={newAccount} onChange={setNewAccount}
            label="CWIP account" placeholder="Capital Work in Progress (1005070000)" />
        </DialogContent>
        <DialogActions>
          <Button data-help="fixedAssets.cwipNew.cancel" onClick={() => setCreating(false)}>Cancel</Button>
          <Button data-help="fixedAssets.cwip.action.create" variant="contained" disabled={!newName.trim() || busy} onClick={createProject}>Create</Button>
        </DialogActions>
      </Dialog>

      <Dialog data-help-page="fixedAssets.cwipDetail.page" open={detail !== null} onClose={() => setDetail(null)} fullWidth maxWidth="md">
        {detail && (
          <>
            <DialogTitle>
              {detail.name} <Chip size="small" label={detail.status} color={STATUS_COLOR[detail.status]} sx={{ ml: 1 }} />
            </DialogTitle>
            <DialogContent>
              <Typography sx={{ mb: 1 }}>Linked cost: <b>{money(detail.linkedTotal)}</b></Typography>
              <TableContainer component={Paper} variant="outlined" sx={{ mb: 2 }}>
                <Table size="small" data-help-section="fixedAssets.cwip.lines">
                  <TableHead>
                    <TableRow>
                      <TableCell data-help="fixedAssets.cwip.ln.date">Date</TableCell><TableCell data-help="fixedAssets.cwip.ln.entry">Entry</TableCell><TableCell data-help="fixedAssets.cwip.ln.desc">Description</TableCell>
                      <TableCell data-help="fixedAssets.cwip.ln.amount" align="right">Amount</TableCell><TableCell data-help="fixedAssets.cwip.ln.actions" />
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {detail.linkedLines.length === 0 && (
                      <TableRow><TableCell colSpan={5} align="center">No cost lines linked</TableCell></TableRow>
                    )}
                    {detail.linkedLines.map(l => (
                      <TableRow key={l.lineId}>
                        <TableCell>{formatDate(l.date)}</TableCell>
                        <TableCell><RouterLink to={`/accounting/journal-entries/${l.journalEntryId}`}>{l.entryNumber}</RouterLink></TableCell>
                        <TableCell>{l.description}</TableCell>
                        <TableCell align="right">{money(l.amount)}</TableCell>
                        <TableCell align="right">
                          {canManage && isOpen && (
                            <IconButton data-help="fixedAssets.cwip.action.unlink" size="small" aria-label="Unlink line" disabled={busy}
                              onClick={() => run(() => CwipService.unlink(detail.id, l.linkId!), 'Line unlinked')}>
                              <DeleteOutlineIcon fontSize="small" />
                            </IconButton>
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>

              {canManage && isOpen && (
                <Box sx={{ mb: 2 }}>
                  <Button data-help="fixedAssets.cwip.action.linkPick" size="small" variant="outlined" onClick={showPicker}>Link cost lines</Button>
                  {linkable && (
                    <Box sx={{ mt: 1 }}>
                      {linkable.length === 0 ? (
                        <Alert severity="info">No unlinked posted lines on the CWIP account.</Alert>
                      ) : (
                        <TableContainer component={Paper} variant="outlined" sx={{ maxHeight: 260 }}>
                          <Table size="small" stickyHeader data-help-section="fixedAssets.cwip.linkable">
                            <TableBody>
                              {linkable.map(l => (
                                <TableRow key={l.lineId} hover onClick={() => toggle(l.lineId)} sx={{ cursor: 'pointer' }}>
                                  <TableCell padding="checkbox"><Checkbox data-help="fixedAssets.cwipDetail.field.pick" checked={picked.has(l.lineId)} /></TableCell>
                                  <TableCell>{formatDate(l.date)}</TableCell>
                                  <TableCell>{l.entryNumber}</TableCell>
                                  <TableCell>{l.description}</TableCell>
                                  <TableCell align="right">{money(l.amount)}</TableCell>
                                </TableRow>
                              ))}
                            </TableBody>
                          </Table>
                        </TableContainer>
                      )}
                      <Button data-help="fixedAssets.cwip.action.link" size="small" sx={{ mt: 1 }} variant="contained" disabled={picked.size === 0 || busy}
                        onClick={() => run(() => CwipService.link(detail.id, Array.from(picked)), 'Lines linked')
                          .then(() => setLinkable(null))}>
                        Link {picked.size} selected
                      </Button>
                    </Box>
                  )}
                </Box>
              )}

              {detail.capitalisation && (
                <Alert severity={detail.capitalisation.status === 'POSTED' ? 'success' : 'info'}>
                  Capitalised {formatDate(detail.capitalisation.date)} for {money(detail.capitalisation.totalAmount)}
                  {' '}into {detail.capitalisation.assetIds.length} asset(s)
                  {detail.capitalisation.status === 'REVERSED' ? ' (reversed)' : ''}.
                  {detail.capitalisation.status === 'POSTED' && !detail.capitalisation.reversible
                    && ' An asset already has depreciation, so this can no longer be reversed.'}
                </Alert>
              )}
            </DialogContent>
            <DialogActions>
              {canManage && isOpen && detail.linkedLineCount === 0 && (
                <Button data-help="fixedAssets.cwip.action.cancel" color="error" disabled={busy} onClick={() => run(() => CwipService.cancel(detail.id), 'Project cancelled')}>Cancel project</Button>
              )}
              {canManage && isOpen && detail.linkedTotal > 0 && (
                <Button data-help="fixedAssets.cwip.action.capitalise" variant="contained" disabled={busy} onClick={() => {
                  setAssetRows([{ name: '', categoryId: '', amount: String(detail.linkedTotal) }]);
                  setCapOpen(true);
                }}>Capitalise</Button>
              )}
              {canManage && detail.status === 'CAPITALISED' && detail.capitalisation?.reversible && (
                <Button data-help="fixedAssets.cwip.action.reverse" color="warning" disabled={busy} onClick={() => run(() => CwipService.reverse(detail.id), 'Capitalisation reversed')}>Reverse capitalisation</Button>
              )}
              <Button data-help="fixedAssets.cwipDetail.close" onClick={() => setDetail(null)}>Close</Button>
            </DialogActions>
          </>
        )}
      </Dialog>

      <Dialog data-help-page="fixedAssets.cwipCap.page" open={capOpen} onClose={() => setCapOpen(false)} fullWidth maxWidth="md">
        <DialogTitle>Capitalise {detail?.name}</DialogTitle>
        <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 2 }}>
          <TextField data-help="fixedAssets.cwipCap.field.date" type="date" label="Capitalisation date" value={capDate} sx={{ mt: 1, maxWidth: 260 }}
            InputLabelProps={{ shrink: true }} onChange={e => setCapDate(e.target.value)} />
          {assetRows.map((r, i) => (
            <Box key={i} sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, alignItems: 'flex-start' }}>
              <TextField data-help="fixedAssets.cwipCap.field.name" label="Asset name" required value={r.name} sx={{ flex: '2 1 200px', maxWidth: 360 }}
                onChange={e => setAssetRows(rs => rs.map((x, j) => j === i ? { ...x, name: e.target.value } : x))} />
              <Box sx={{ flex: '2 1 200px', maxWidth: 320 }}>
                <SearchableSelect<FixedAssetCategory> data-help="fixedAssets.cwipCap.field.category" options={categories} value={r.categoryId}
                  label="Category" getOptionLabel={c => c.name}
                  onChange={id => setAssetRows(rs => rs.map((x, j) => j === i ? { ...x, categoryId: id } : x))} />
              </Box>
              <TextField data-help="fixedAssets.cwipCap.field.amount" label="Amount" type="number" required value={r.amount} sx={{ flex: '1 1 140px', maxWidth: 220 }}
                onChange={e => setAssetRows(rs => rs.map((x, j) => j === i ? { ...x, amount: e.target.value } : x))} />
              {assetRows.length > 1 && (
                <IconButton data-help="fixedAssets.cwip.action.removeAsset" aria-label="Remove asset" onClick={() => setAssetRows(rs => rs.filter((_, j) => j !== i))}>
                  <DeleteOutlineIcon />
                </IconButton>
              )}
            </Box>
          ))}
          <Box><Button data-help="fixedAssets.cwip.action.addAsset" size="small" onClick={() => setAssetRows(rs => [...rs, { name: '', categoryId: '', amount: '' }])}>Add asset</Button></Box>
          <Alert severity={capOk ? 'success' : 'warning'}>
            Assets total {money(assetSum)} of {money(detail?.linkedTotal ?? 0)} linked cost
            {capOk ? '' : ' - the amounts must add up exactly.'}
          </Alert>
        </DialogContent>
        <DialogActions>
          <Button data-help="fixedAssets.cwipCap.cancel" onClick={() => setCapOpen(false)}>Cancel</Button>
          <Button data-help="fixedAssets.cwip.action.capConfirm" variant="contained" disabled={!capOk || busy} onClick={capitalise}>Capitalise</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default CwipProjects;
